import React from 'react'
import { UseFormReturn } from 'react-hook-form'
import { H2, Line, Button, Flex } from '../../lib/components'
import { FileUploadIcon, FileUploadedIcon } from '../../lib/components/Icon'
import type { ComingSoonFormData } from '../../services/coming-soon-admin-service'
import { CircularProgress } from '@mui/material'
import './coming-soon-form.scss'

export type ComingSoonFormPresenterProps = {
  form: UseFormReturn<ComingSoonFormData>
  isEditMode: boolean
  isSubmitting: boolean
  isLoading: boolean
  coverPreview: string
  onSubmit: (data: ComingSoonFormData) => Promise<void>
  onFileUpload: (file: File) => Promise<void>
}

export function ComingSoonFormPresenter({
  form,
  isEditMode,
  isSubmitting,
  isLoading,
  coverPreview,
  onSubmit,
  onFileUpload,
}: ComingSoonFormPresenterProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = form

  const coverKey = watch('cover_image')

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) void onFileUpload(file)
    e.target.value = ''
  }

  return (
    <>
      {isLoading && (
        <div className='loading-overlay'>
          <CircularProgress size={40} color='primary' className='loading-overlay__spinner' />
        </div>
      )}
      <Flex variant='column-center'>
        <H2 classes={['mb-24']} text={isEditMode ? 'Edit coming soon' : 'Add coming soon'} />
        <form className='coming-soon-form' onSubmit={handleSubmit(onSubmit)} noValidate>
          <input
            type='hidden'
            {...register('cover_image', { required: 'Cover image is required' })}
          />
          <Line />
          <div className='coming-soon-form__field'>
            <label className='coming-soon-form__label' htmlFor='cover'>
              Cover image <span className='coming-soon-form__required'>*</span>
            </label>
            <p className='coming-soon-form__hint'>JPEG/PNG, max 10 MB</p>
            <div className='coming-soon-form__upload-row'>
              <label htmlFor='cover' className='coming-soon-form__file-label'>
                <FileUploadIcon />
                <span>Upload</span>
              </label>
              <input
                id='cover'
                type='file'
                accept='.jpg,.jpeg,.png,image/jpeg,image/png'
                className='coming-soon-form__file-input'
                onChange={handleFileChange}
              />
              {coverKey ? (
                <span className='coming-soon-form__file-ok'>
                  <FileUploadedIcon /> Image attached
                </span>
              ) : null}
            </div>
            {errors.cover_image ? (
              <p className='coming-soon-form__error'>{errors.cover_image.message}</p>
            ) : null}
            {coverPreview ? (
              <div className='coming-soon-form__preview-wrap'>
                <img
                  src={coverPreview}
                  alt=''
                  className='coming-soon-form__preview'
                  width={320}
                  height={200}
                />
              </div>
            ) : null}
          </div>

          <div className='coming-soon-form__field'>
            <label className='coming-soon-form__label' htmlFor='title'>
              Opportunity title <span className='coming-soon-form__required'>*</span>
            </label>
            <input
              id='title'
              type='text'
              className={`coming-soon-form__input ${
                errors.title ? 'coming-soon-form__input--error' : ''
              }`}
              placeholder='Shown as the headline on the Early Look carousel'
              {...register('title', { required: 'Title is required' })}
            />
            {errors.title ? (
              <p className='coming-soon-form__error'>{errors.title.message}</p>
            ) : null}
          </div>

          <div className='coming-soon-form__field'>
            <label className='coming-soon-form__label' htmlFor='description'>
              Description <span className='coming-soon-form__required'>*</span>
            </label>
            <textarea
              id='description'
              className={`coming-soon-form__textarea ${
                errors.description ? 'coming-soon-form__textarea--error' : ''
              }`}
              rows={5}
              placeholder='Supporting text under the headline on the website'
              {...register('description', { required: 'Description is required' })}
            />
            {errors.description ? (
              <p className='coming-soon-form__error'>{errors.description.message}</p>
            ) : null}
          </div>

          <div className='coming-soon-form__field coming-soon-form__field--inline'>
            <label className='coming-soon-form__label' htmlFor='sort_order'>
              Sort order
            </label>
            <input
              id='sort_order'
              type='number'
              min={0}
              className='coming-soon-form__input coming-soon-form__input--narrow'
              {...register('sort_order', { valueAsNumber: true })}
            />
          </div>

          <div className='coming-soon-form__field coming-soon-form__field--checkbox'>
            <label htmlFor='is_published' className='coming-soon-form__checkbox-label'>
              <input id='is_published' type='checkbox' {...register('is_published')} />
              <span>Published (visible on public site)</span>
            </label>
          </div>

          <div className='coming-soon-form__actions'>
            <Button
              text={isSubmitting ? 'Saving…' : 'Save'}
              variant='basic-btn'
              buttonType='submit'
              isDisabled={isSubmitting}
              dataTestId='coming-soon-form-submit'
              handleClick={() => {}}
            />
          </div>
        </form>
      </Flex>
    </>
  )
}
